import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';
import { createStore, del, set, values, type UseStore } from 'idb-keyval';
import { clientId } from './idempotency';
import {
  uploadImage,
  uploadManagedImage,
  UploadError,
  type UploadPayload,
} from './image';
import type { MediaHoldCode, MediaUploadReceipt } from '@servwell/shared';
import { isPermanentRefusal } from './refusal';
import { queryClient } from './query';
import { sameAccount, sameAccountOrNobody, type AccountRef } from './account';
import { onSignalOrVisible } from './browser';

/**
 * Photos taken with no signal (§4 Wave 1).
 *
 * Everything else a tech does offline is a tRPC mutation, so TanStack pauses it, writes it to
 * IndexedDB and replays it. A photo was the one exception: `uploadImage` was a bare `fetch`,
 * which cannot pause, is not in `REPLAYABLE`, and is never persisted. The card printed
 * `Failed to fetch` directly above its own promise that *"what you photograph today is what
 * you can prove in six months"* — and the photograph was gone. Worse, `attachment.remove` IS
 * replayable, so a tech in a crawlspace could delete a photo and could not take one.
 *
 * ## Why this is not a tRPC mutation
 *
 * Making the upload a mutation would have handed it the whole existing queue for free, and it
 * is the wrong trade three ways. Bytes have to reach a JSON-RPC batch as base64, which is a
 * third more of the LTE that `prepareImage` exists to save. A batch fails whole. And the
 * persister serialises the *entire* dehydrated client into one IndexedDB value on every
 * mutation state change — a dozen queued photos would turn each tick of a checklist into a
 * multi-megabyte write on a phone. So photos get their own store, holding `Blob`s natively.
 *
 * ## What it promises
 *
 * A photo is written to this queue BEFORE any network is touched — there is no
 * try-then-fall-back-to-queueing path, because a photo that is only safe once a request has
 * failed is a photo that is not safe while the request is in flight. It appears in the grid
 * immediately, from its own bytes, marked as waiting. It leaves the queue when the server has
 * said 201 and not one moment earlier.
 *
 * ## What it refuses to do
 *
 * **It never drops a photo to make room.** A cap that evicts silently is the defect this file
 * exists to remove, wearing a hat. When the queue is full the *new* photo is refused, loudly,
 * while the tech is still standing there and can do something about it.
 */

/** One photo waiting on this phone. Its `mutationId` is also its key in the store. */
export interface QueuedUpload extends UploadPayload {
  byteSize: number;
  queuedAt: number;
  /**
   * Who took it. The store is per-ORIGIN, not per-account, so a shop tablet that two techs
   * sign into shares one queue — and a photo drained under the wrong cookie is attributed to
   * the wrong person, or lands in another org as an orphan attached to an id that means
   * nothing there. Stamped here so the drain can refuse to send somebody else's work.
   */
  userId: string;
  orgId: string;
  /**
   * Set only when the server has refused this photo for a reason that retrying cannot fix —
   * the stop was reassigned, the job was deleted, the file is too big. It stays in the queue
   * with the refusal on it, because the alternative is deleting a technician's photograph
   * because a server said no once.
   */
  failure: string | null;
  holdCode?: MediaHoldCode;
  retryAt?: number;
  /**
   * Taken in a form that has not been sent yet ("Flag a problem", ticket 03): kept on the
   * phone and shown in the form's grid, but never sent, because the ticket it names does not
   * exist anywhere until the person sends the form. `release` lets it go when they do;
   * `discard` throws it away when they discard the form. Not counted as waiting on a
   * network: it is waiting on a person.
   */
  parked?: boolean;
}

/**
 * A queued photo as the screen sees it. `sending` is memory-only and deliberately not part of
 * the durable record: an app killed mid-upload would otherwise come back with a photo marked
 * as on the wire forever, and nothing would ever be on that wire again.
 */
export type QueuedUploadView = QueuedUpload & { sending: boolean };

/** Durable side of the queue. An interface so the logic is testable without IndexedDB. */
export interface UploadStore {
  all(): Promise<QueuedUpload[]>;
  put(item: QueuedUpload): Promise<void>;
  remove(mutationId: string): Promise<void>;
}

/**
 * Unsent photographs consume room by their actual original bytes. A large original may
 * use most of this allowance; admissions refuse the next file without evicting earlier work.
 */
export const MAX_QUEUE_BYTES = 64 * 1024 * 1024;

/** Refusing a photo we cannot promise to keep. Thrown at the moment of taking it. */
export class QueueFullError extends Error {
  constructor(waiting: number) {
    super(
      `This phone is already holding ${waiting} ${waiting === 1 ? 'photo' : 'photos'} that ` +
        `haven’t sent. Get some signal so those go through before adding more.`,
    );
    this.name = 'QueueFullError';
  }
}

const byQueuedAt = (a: QueuedUpload, b: QueuedUpload) => a.queuedAt - b.queuedAt;

/**
 * How many "not now" answers a photo with nothing on the phone to wait for gets before it is
 * marked as never sent: enough for a flag queued in another tab to land, not enough to keep
 * spending a tech's signal on bytes nothing will take.
 */
export const NOT_NOW_TRIES = 3;

export class UploadQueue {
  readonly #store: UploadStore;
  readonly #send: (payload: UploadPayload) => Promise<{ id: string }>;
  readonly #onDelivered: () => void;
  /** Must this photo wait for something still on the phone? (`holdWith`) */
  #awaiting: (item: QueuedUpload) => boolean;
  /** The refusal a photo told "not now" with nothing on the phone to wait for ends with. */
  #neverReady: string;
  /** "Not now" answers per photo with nothing to wait for, this session (the backstop). */
  readonly #notNow = new Map<string, number>();
  /** Photos given back while on the wire: parked (into the record named) when they come back. */
  readonly #parkWhenBack = new Map<string, string>();

  /**
   * The in-memory mirror is the read model, not a cache: `useSyncExternalStore` needs a
   * snapshot it can take synchronously, and a grid that had to await IndexedDB would show a
   * tech an empty space where their photo is.
   */
  readonly #items = new Map<string, QueuedUpload>();
  /** On the wire right now — cannot be taken back, because it is going to land. */
  readonly #inFlight = new Set<string>();
  /** One user-requested recovery attempt, never persisted or reused by automatic retries. */
  readonly #recoverOnNextSend = new Set<string>();
  #snapshot: QueuedUploadView[] = [];
  readonly #listeners = new Set<() => void>();
  #draining = false;
  #loading: Promise<void> | null = null;
  #retry: ReturnType<typeof setTimeout> | undefined;
  #backoffMs = 0;
  #account: AccountRef | null = null;
  /** The network has confirmed `#account` (not just the saved one, drawn offline). */
  #confirmed = false;
  #stopped = false;
  /** Bytes whose local write is in progress also consume queue room. */
  #admittingBytes = 0;

  constructor(opts: {
    store: UploadStore;
    send?: (payload: UploadPayload) => Promise<{ id: string }>;
    /** Called when a photo has actually landed, so the server's own list can catch up. */
    onDelivered?: () => void;
    /**
     * Must this photo wait for a write still on the phone? A photo taken with "Flag a
     * problem" waits for its `ticket.flag` (`lib/ticket-photos.ts`). Held photos are skipped,
     * not sent, and the queue comes back for them on its timer.
     */
    awaiting?: (item: QueuedUpload) => boolean;
    /** What a photo that keeps being told "not now", with nothing to wait for, is marked. */
    neverReady?: string;
  }) {
    this.#store = opts.store;
    this.#send = opts.send ?? uploadImage;
    this.#onDelivered = opts.onDelivered ?? (() => {});
    this.#awaiting = opts.awaiting ?? (() => false);
    this.#neverReady =
      opts.neverReady ?? 'The record this photo was for was never saved.';
  }

  /**
   * The rule for photos that wait on something else on the phone, for the app's one queue
   * (built before the writes it waits on exist). `lib/ticket-photos.ts` sets it at start.
   */
  holdWith(rules: {
    awaiting: (item: QueuedUpload) => boolean;
    neverReady: string;
  }): void {
    this.#awaiting = rules.awaiting;
    this.#neverReady = rules.neverReady;
  }

  /**
   * Tell the queue whose session it is running under.
   *
   * Until this is known the queue will not send anything: an upload authenticates with
   * whatever cookie the browser has at the moment it drains, so sending before the app knows
   * who is signed in is how one tech's photograph is filed under another's name.
   *
   * `confirmed`: the network has said this is who is signed in. The saved account, drawn
   * after an offline start, shows its photos but sends none until then: the queued writes'
   * rule, for the same reason (MAP 225b).
   */
  setAccount(account: AccountRef | null, confirmed = false): void {
    const changed = !sameAccountOrNobody(account, this.#account);
    this.#account = account;
    this.#confirmed = account !== null && confirmed;
    if (changed) {
      this.#recoverOnNextSend.clear();
      this.#publish();
    }
  }

  /**
   * Another tab changed who is signed in, or this one is signing out: nothing drains from
   * here again (whatever cookie the browser holds next is not this account's). The photos
   * stay on the device for their account.
   */
  stop(): void {
    this.#stopped = true;
    this.setAccount(null);
    this.stopRetrying();
  }

  /** Whether this photo belongs to the session now signed in. */
  #mine = (item: QueuedUpload): boolean => sameAccount(this.#account, item);

  /**
   * This account's photos on the device, read fresh from the disk (another tab's too): still
   * to send, and refused. The sign-out warning's count. Best effort.
   */
  async countFor(account: AccountRef): Promise<{ waiting: number; refused: number }> {
    let waiting = 0;
    let refused = 0;
    for (const item of await this.#store.all()) {
      if (!sameAccount(account, item)) continue;
      if (item.failure) refused += 1;
      else waiting += 1;
    }
    return { waiting, refused };
  }

  /** Read the durable store into memory. Idempotent — the first call does the work. */
  load(): Promise<void> {
    this.#loading ??= this.#store.all().then(
      (items) => {
        for (const item of items) this.#items.set(item.mutationId, item);
        this.#publish();
      },
      (err) => {
        // Memoising the REJECTION would leave the queue empty, silent and undrainable for
        // the rest of the session, with a phone full of photographs behind it.
        this.#loading = null;
        throw err;
      },
    );
    return this.#loading;
  }

  /** Newest first, matching the order the server's own photo list comes back in. */
  snapshot(): QueuedUploadView[] {
    return this.#snapshot;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /** Photos still expected to send. A refused one is waiting for a person, not a network. */
  pendingCount(): number {
    let n = 0;
    for (const item of this.#items.values())
      if (!item.failure && !item.parked && this.#mine(item)) n += 1;
    return n;
  }

  /** Photos the server has refused outright. Waiting on a person, not on a network. */
  refusedCount(): number {
    let n = 0;
    for (const item of this.#items.values()) if (item.failure && this.#mine(item)) n += 1;
    return n;
  }

  bytesHeld(): number {
    let n = 0;
    for (const item of this.#items.values()) n += item.byteSize;
    return n;
  }

  /**
   * Take a photo into the queue. Resolves only once it is durably stored — the caller is
   * telling a tech "kept", and it must not say so before it is true.
   */
  async add(
    payload: Omit<UploadPayload, 'clientId' | 'mutationId'> & {
      clientId?: string;
      mutationId?: string;
      userId: string;
      orgId: string;
      parked?: boolean;
    },
  ): Promise<QueuedUpload> {
    await this.load();
    const byteSize = payload.blob.size;
    if (this.bytesHeld() + this.#admittingBytes + byteSize > MAX_QUEUE_BYTES) {
      throw new QueueFullError(this.#items.size);
    }
    const item: QueuedUpload = {
      ...payload,
      clientId: payload.clientId ?? clientId(),
      mutationId: payload.mutationId ?? crypto.randomUUID(),
      byteSize,
      queuedAt: Date.now(),
      failure: null,
      ...(payload.parked ? { parked: true } : {}),
    };
    // Durable first. If the phone's storage refuses this — which is what a full disk looks
    // like — nothing enters the read model, and the caller's `catch` is the truth.
    this.#admittingBytes += byteSize;
    try {
      await this.#store.put(item);
      this.#items.set(item.mutationId, item);
    } finally {
      this.#admittingBytes -= byteSize;
    }
    this.#publish();
    void this.drain();
    return item;
  }

  /**
   * Take a photo back. No server call: it never got there, so there is nothing to undo.
   *
   * Except while it is on the wire, when there is — the send will land whatever this does, and
   * dropping the record would make the photo disappear from the grid and then return from the
   * server a second later. The screen disables the control for exactly that moment; this is
   * the same rule stated where it is enforced rather than only where it is drawn.
   */
  async drop(mutationId: string): Promise<void> {
    if (this.#inFlight.has(mutationId)) return;
    const item = this.#items.get(mutationId);
    if (!item || !this.#mine(item)) return;
    // Durable first, the same way round as `add`. Taking it off the screen before the phone
    // has actually forgotten it means a removal that failed still LOOKS done — and the photo
    // comes back at the next reload, after the tech has retaken it.
    await this.#store.remove(mutationId);
    this.#items.delete(mutationId);
    this.#recoverOnNextSend.delete(mutationId);
    this.#publish();
  }

  /**
   * Send what is waiting, oldest first.
   *
   * Serial on purpose, exactly as `PhotoGrid` has always been: four photos in parallel over
   * one bar of LTE finish later than four in turn, and a serial queue can say which one
   * failed. The guard is not an optimisation — two drains would send every photo twice, and
   * only the server's idempotency key would save it.
   */
  async drain(): Promise<void> {
    // Claimed synchronously, BEFORE any await. Taking it after `load()` leaves a window on
    // a cold start where two drains — the boot one and a visibility change — both pass the
    // check and send every photo twice. The server's key stops a second row; it does not stop
    // a second upload of the bytes over a tech's LTE.
    if (this.#draining) return;
    this.#draining = true;
    try {
      // A signal arrived, so the timer has nothing left to do. Leaving it armed would stack a
      // second drain behind this one and halve the effective backoff every time.
      this.#clearRetry();
      if (this.#stopped || !this.#confirmed || !onlineManager.isOnline()) return;
      await this.load();
      // A photo held for a write still on the phone, or told "not now": come back for it.
      let held = false;
      let earliestRetry = Infinity;
      for (const item of [...this.#items.values()].sort(byQueuedAt)) {
        // Re-read: a tech can remove a photo, or its refusal can be recorded, while the one
        // in front of it is still on the wire.
        const live = this.#items.get(item.mutationId);
        if (!live || live.failure || live.parked) continue;
        // Somebody else's photo, waiting for them to sign back in on this device.
        if (!this.#mine(live)) continue;
        if (this.#stopped || !this.#confirmed) return;
        if (live.retryAt && live.retryAt > Date.now()) {
          held = true;
          earliestRetry = Math.min(earliestRetry, live.retryAt - Date.now());
          continue;
        }
        const outcome = await this.#sendOne(live);
        if (outcome === 'stop') return;
        if (outcome === 'held') held = true;
        if (outcome === 'sent') {
          this.#backoffMs = 0;
          this.#onDelivered();
        }
      }
      if (held) this.#scheduleRetry(Number.isFinite(earliestRetry) ? earliestRetry : 0);
    } finally {
      this.#draining = false;
    }
  }

  /**
   * One photo, once. `sent`: it landed and left the queue. `held`: it waits for something
   * still on the phone, or was told "not now"; skipped, and come back for on the timer.
   * `refused`: marked with the reason, waiting for a person. `stop`: no connection, or the
   * server is unwell, or the phone would not forget a sent photo; the drain stops and the
   * timer is armed.
   */
  async #sendOne(item: QueuedUpload): Promise<'sent' | 'held' | 'refused' | 'stop'> {
    // Its ticket's flag has not landed (ticket 03): sending now could only be told "not now",
    // and would spend the bytes to hear it. Skipped, not stopped: the photos behind it are not
    // waiting for that flag.
    if (this.#awaiting(item)) return 'held';
    this.#inFlight.add(item.mutationId);
    this.#publish();
    try {
      if (item.protocol === 'managed-photo-v1') {
        await uploadManagedImage(item, {
          retryUnknown: this.#recoverOnNextSend.delete(item.mutationId),
          beforeRequest: () => {
            if (this.#stopped || !this.#confirmed || !this.#mine(item)) {
              throw new UploadError(
                'Sign in again to send your photo. It stays on this phone.',
                401,
              );
            }
          },
          checkpoint: (receipt) => this.#checkpoint(item.mutationId, receipt),
        });
      } else {
        await this.#send(item);
      }
    } catch (err) {
      const parkInto = this.#parkWhenBack.get(item.mutationId);
      if (parkInto !== undefined) {
        // Given back while it was on the wire, and it did not land: parked, whatever the answer.
        this.#parkWhenBack.delete(item.mutationId);
        await this.#parkNow(this.#items.get(item.mutationId) ?? item, parkInto);
        return 'held';
      }
      if (item.protocol === 'managed-photo-v1' && err instanceof UploadError) {
        if (err.status === 401) return 'stop'; // wait for confirmed identity; never loop a stale session
        if (err.pause) {
          await this.#markFailed(item.mutationId, err.message, err.code);
          return 'refused';
        }
        if (err.status === 429) {
          const live = this.#items.get(item.mutationId);
          const delay = Math.max(1000, err.retryAfterMs ?? 30_000);
          if (live) {
            const deferred = { ...live, retryAt: Date.now() + delay, holdCode: err.code };
            await this.#store.put(deferred);
            this.#items.set(item.mutationId, deferred);
          }
          this.#scheduleRetry(delay);
          return 'stop';
        }
      }
      if (err instanceof UploadError && err.retryLater) return this.#toldNotNow(item);
      if (err instanceof UploadError && isPermanentRefusal(err.status)) {
        await this.#markFailed(item.mutationId, err.message);
        return 'refused';
      }
      this.#scheduleRetry();
      return 'stop'; // no connection, or the server is unwell — try again on a timer.
    } finally {
      // Publish here, not only on the success path: a refused or interrupted photo that
      // stayed marked as sending would leave its Remove button disabled for good.
      this.#inFlight.delete(item.mutationId);
      this.#publish();
    }
    this.#notNow.delete(item.mutationId);
    this.#parkWhenBack.delete(item.mutationId); // it landed: there is nothing to park
    // Same order, same reason. If the phone will not forget it, the photo simply sends again
    // at the next signal and the server's idempotency key absorbs it — which is a far better
    // outcome than a grid that has dropped its own copy and never asked the server for the
    // one that landed.
    try {
      await this.#store.remove(item.mutationId);
    } catch {
      // The photo is on the server and still on the phone. It sends again and the key
      // absorbs it — but only if something comes back to try, so schedule that.
      this.#scheduleRetry();
      return 'stop';
    }
    this.#items.delete(item.mutationId);
    this.#publish();
    return 'sent';
  }

  async #checkpoint(mutationId: string, receipt: MediaUploadReceipt): Promise<void> {
    const live = this.#items.get(mutationId);
    if (!live) throw new Error('The local photo is no longer available');
    const updated = { ...live, uploadId: receipt.uploadId, receipt, retryAt: undefined };
    await this.#store.put(updated);
    this.#items.set(mutationId, updated);
    this.#publish();
  }

  /**
   * The server said "not now" (a ticket this tech cannot attach to yet). Asked again while
   * something on the phone may still make it so (`#awaiting`, normally checked before the
   * send; this covers a flag that went back on the phone meanwhile), and otherwise only
   * `NOT_NOW_TRIES` times this session (a flag in another tab, say), then marked with
   * `#neverReady`: waiting for a person, out of the waiting count, offering Remove.
   */
  async #toldNotNow(item: QueuedUpload): Promise<'held' | 'refused'> {
    if (this.#awaiting(item)) return 'held';
    const tries = (this.#notNow.get(item.mutationId) ?? 0) + 1;
    this.#notNow.set(item.mutationId, tries);
    if (tries < NOT_NOW_TRIES) return 'held';
    this.#notNow.delete(item.mutationId);
    await this.#markFailed(item.mutationId, this.#neverReady);
    return 'refused';
  }

  /**
   * Come back to a stopped queue on our own.
   *
   * Without this the drain waits for the next connectivity change, visibility change or new
   * photo — so after a 500 the banner reads "Sending 3 things…" over a queue that is not
   * sending and has nothing scheduled to make it. Backoff and cap match the mutation
   * defaults in `query.ts`, because it is the same problem and there is no reason for a tech
   * to meet two different rhythms.
   */
  #scheduleRetry(minimumMs = 0): void {
    if (this.#retry) return;
    this.#backoffMs =
      this.#backoffMs === 0 ? 1000 : Math.min(this.#backoffMs * 2, 30_000);
    this.#retry = setTimeout(
      () => {
        this.#retry = undefined;
        void this.drain();
      },
      Math.max(this.#backoffMs, minimumMs),
    );
    // Node only, and only so a test process is not held open by a queue waiting to try again.
    (this.#retry as unknown as { unref?: () => void }).unref?.();
  }

  /** Stop waiting to try again. Called when something else is about to, and on teardown. */
  #clearRetry(): void {
    if (this.#retry) clearTimeout(this.#retry);
    this.#retry = undefined;
  }

  /** Let go of the timer. The queue's contents are on disk and outlive this object. */
  stopRetrying(): void {
    this.#clearRetry();
  }

  /**
   * The form these photos were taken in has been sent ("Flag a problem"): they may go now,
   * after it. Durable first, so a restart cannot bring them back parked.
   */
  async release(entityType: string, entityId: string): Promise<void> {
    await this.load();
    for (const item of [...this.#items.values()]) {
      if (!item.parked || item.entityType !== entityType || item.entityId !== entityId) {
        continue;
      }
      const { parked: _parked, ...released } = item;
      await this.#store.put(released);
      this.#items.set(item.mutationId, released);
    }
    this.#publish();
    await this.drain();
  }

  /**
   * Back to waiting on a person: the write these photos were for will not be delivered from
   * this phone ("Flag a problem" refused before it was kept, or dismissed from the changes
   * panel). Unsent photos of the record are parked again, any refusal cleared; with `to`, they
   * move to that record first (another draft is being written in the slot). Durable first.
   *
   * ⚠️ One on the wire is parked when its answer comes, unless it landed: a drain that started
   * a moment before the flag was given back (its hold had just lifted) must not leave the photo
   * outside the draft, asking "not now" until it is given up (found by the browser check).
   */
  async park(entityType: string, entityId: string, to: string = entityId): Promise<void> {
    await this.load();
    for (const item of [...this.#items.values()]) {
      if (item.entityType !== entityType || item.entityId !== entityId) continue;
      if (this.#inFlight.has(item.mutationId)) {
        this.#parkWhenBack.set(item.mutationId, to);
        continue;
      }
      await this.#parkNow(item, to);
    }
    this.#publish();
  }

  async #parkNow(item: QueuedUpload, to: string): Promise<void> {
    const parked: QueuedUpload = { ...item, entityId: to, failure: null, parked: true };
    await this.#store.put(parked);
    this.#items.set(item.mutationId, parked);
    this.#notNow.delete(item.mutationId);
  }

  /**
   * The form these photos were taken in was discarded ("Flag a problem" → Discard): so are
   * they, parked or already released behind a flag that was refused, because the record they
   * name is being thrown away and nothing would ever take them (a photo left behind would ask
   * the server "not yet?" for ever). One on the wire is left to land, as `drop` does. Durable
   * first.
   */
  async discard(entityType: string, entityId: string): Promise<void> {
    await this.load();
    for (const item of [...this.#items.values()]) {
      if (item.entityType !== entityType || item.entityId !== entityId) continue;
      if (this.#inFlight.has(item.mutationId)) continue;
      await this.#store.remove(item.mutationId);
      this.#items.delete(item.mutationId);
    }
    this.#publish();
  }

  /** Let a tech ask again for a photo the server refused — the reason may have gone away. */
  async retry(mutationId: string): Promise<void> {
    const item = this.#items.get(mutationId);
    if (!item?.failure || !this.#mine(item)) return;
    const cleared = { ...item, failure: null, holdCode: undefined };
    this.#notNow.delete(mutationId);
    await this.#store.put(cleared);
    this.#items.set(mutationId, cleared);
    if (item.protocol === 'managed-photo-v1') this.#recoverOnNextSend.add(mutationId);
    this.#publish();
    await this.drain();
  }

  async #markFailed(
    mutationId: string,
    failure: string,
    holdCode?: MediaHoldCode,
  ): Promise<void> {
    const item = this.#items.get(mutationId);
    if (!item) return;
    const marked = { ...item, failure, holdCode };
    this.#items.set(mutationId, marked);
    this.#publish();
    await this.#store.put(marked);
  }

  /** Rebuild the snapshot once per change, so `getSnapshot` can be identity-stable. */
  #publish(): void {
    // The signed-in account's photos only. Another tech's work is still on the device and
    // still safe; it is not this person's to see, to count, or to remove.
    this.#snapshot = [...this.#items.values()]
      .filter(this.#mine)
      .sort(byQueuedAt)
      .reverse()
      .map((item) => ({ ...item, sending: this.#inFlight.has(item.mutationId) }));
    for (const listener of this.#listeners) listener();
  }
}

// ── The real store ───────────────────────────────────────────────────────────

let idb: UseStore | undefined;
/** Opened lazily: this module is imported by tests that run in Node, where there is none. */
const store = (): UseStore => (idb ??= createStore('servwell-uploads', 'photos'));

/**
 * Its own database, not the one the query cache lives in. A photo must not be evicted by
 * cache housekeeping, and the cache must not be rewritten because a photo arrived.
 */
export const indexedDbUploadStore: UploadStore = {
  all: () => values<QueuedUpload>(store()),
  put: (item) => set(item.mutationId, item, store()),
  remove: (mutationId) => del(mutationId, store()),
};

export const uploadQueue = new UploadQueue({
  store: indexedDbUploadStore,
  // A photo that just landed is not in any cached list yet, and the grid it landed in is
  // very likely the one on screen.
  onDelivered: () => void queryClient.invalidateQueries(),
});

/**
 * Ask the browser to stop treating this origin's storage as disposable.
 *
 * IndexedDB is best-effort by default: a user agent under storage pressure is entitled to
 * clear it, and it would take the day's photographs with it. `persist()` is the only lever
 * there is, it may be refused, and it costs nothing to ask — so it is asked once, at start,
 * rather than in the middle of somebody taking a picture.
 */
async function requestPersistentStorage(): Promise<void> {
  try {
    await globalThis.navigator?.storage?.persist?.();
  } catch {
    // Refused, unsupported, or blocked by settings. Best effort was always the ceiling.
  }
}

/**
 * Start draining, on the same three signals the rest of the app uses.
 *
 * `load().then(drain)` is the one that took a session to learn the first time (§4.8): the
 * connectivity subscription fires on a CHANGE, and opening the app fresh while already online
 * is not a change. Without it a photo taken in a basement waits for the tech to lose signal
 * again before it is ever sent.
 */
export function startUploadQueue(queue: UploadQueue = uploadQueue): () => void {
  void requestPersistentStorage();
  void queue.load().then(() => queue.drain());
  const stopListening = onSignalOrVisible(() => void queue.drain());
  return () => {
    stopListening();
    queue.stopRetrying();
  };
}

// ── Reading it from React ────────────────────────────────────────────────────

export function useUploadQueue(queue: UploadQueue = uploadQueue): QueuedUploadView[] {
  const getSnapshot = useCallback(() => queue.snapshot(), [queue]);
  return useSyncExternalStore(queue.subscribe, getSnapshot, getSnapshot);
}

/** The photos waiting for one job or one stop — what its grid has to show. */
export function useQueuedPhotos(
  entityType: string,
  entityId: string,
): QueuedUploadView[] {
  const all = useUploadQueue();
  return useMemo(
    () => all.filter((p) => p.entityType === entityType && p.entityId === entityId),
    [all, entityType, entityId],
  );
}

/**
 * Photos counted alongside the mutation queue in the connection banner.
 *
 * Not optional. The banner promises *"everything you do is saved on this phone and sends
 * itself when you're back"*, and then says *"everything you did offline has gone through"* the
 * moment its count reaches zero. A queue this one cannot see would make both sentences false
 * while photographs were still sitting on the phone.
 */
export function useQueuedPhotoCount(): number {
  const all = useUploadQueue();
  return useMemo(() => all.filter((p) => !p.failure && !p.parked).length, [all]);
}

/**
 * Photos the server refused outright.
 *
 * Read by the banner for one reason: without it, a refusal takes the waiting count from one
 * to zero, which is indistinguishable from the queue finishing — and the app announces
 * "everything you did offline has gone through" over a photograph it was just told it could
 * not have.
 */
export function useRefusedPhotoCount(): number {
  const all = useUploadQueue();
  return useMemo(() => all.filter((p) => p.failure).length, [all]);
}
